import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AiOutlineClose } from 'react-icons/ai';
import { FaUser, FaDownload } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { PROFILE_DESCRIPTIONS } from '../../utils/profileDescriptions';
import Tooltip from '../Tooltip/Tooltip';
import useQuizAttempt from '../../hooks/quiz/useQuizAttempt';
import downloadPDF from '../../utils/pdf/downloadPDF';
import AthleteTraitPDF from '../../utils/pdf/AthleteTraitPDF';
import globalButtonStyles from '../../styles/ButtonGradient.module.css';
import styles from './ViewTraitQuizDetailsButton.module.css';

const ARCHETYPE_CONFIG = {
  "The Quiet Craftsman":      { gradient: "linear-gradient(160deg, #2d3748 0%, #7B8FA1 100%)" },
  "The Assertive Competitor": { gradient: "linear-gradient(160deg, #7c2d12 0%, #E85D04 100%)" },
  "The Disciplined Driver":   { gradient: "linear-gradient(160deg, #14532d 0%, #2D9D5A 100%)" },
  "The Resilient Maverick":   { gradient: "linear-gradient(160deg, #4c1d95 0%, #9B5DE5 100%)" },
  "The Adaptive Learner":     { gradient: "linear-gradient(160deg, #03045e 0%, #0096C7 100%)" },
  "The Steady Supporter":     { gradient: "linear-gradient(160deg, #713f12 0%, #C9A227 100%)" },
  "The Coach's Mirror":       { gradient: "linear-gradient(160deg, #9a3412 0%, #F4A261 100%)" },
};

const DEFAULT_GRADIENT = "linear-gradient(160deg, #1a1a2e 0%, #4a4a6a 100%)";

const ViewTraitQuizDetailsButton = ({ attemptId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [traitsExpanded, setTraitsExpanded] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const TOP_COUNT = 10;

  const athleteProfile = useSelector(state => state.athlete?.profile);
  const coachProfile = useSelector(state => state.coach?.profile);
  const selectedAthlete = useSelector(state => state.coach?.selectedAthlete);
  const athleteAssessments = useSelector(state => state.athlete?.assessments || []);
  const coachAssessments = useSelector(state => state.coach?.assessments || []);
  const selectedAthleteAssessments = useSelector(state => state.coach?.selectedAthleteAssessments || []);

  const athleteName = selectedAthlete
    ? `${selectedAthlete.firstName} ${selectedAthlete.lastName}`
    : athleteProfile
      ? `${athleteProfile.firstName} ${athleteProfile.lastName}`
      : coachProfile
        ? `${coachProfile.firstName} ${coachProfile.lastName}`
        : 'Athlete';

  const relevantAssessments = selectedAthlete
    ? selectedAthleteAssessments
    : athleteProfile
      ? athleteAssessments
      : coachAssessments;

  const gameIQAttempts = relevantAssessments.filter(a => a.type === 'game-iq');

  const handleDownloadPDF = async () => {
    if (!attempt) return;
    setPdfLoading(true);
    try {
      const safeName = athleteName.replace(/\s+/g, '_');
      await downloadPDF(
        <AthleteTraitPDF attempt={attempt} athleteName={athleteName} gameIQAttempts={gameIQAttempts} />,
        `${safeName}_Trait_Report.pdf`
      );
    } finally {
      setPdfLoading(false);
    }
  };
  const { fetchAttempt, attempt, loading, error } = useQuizAttempt();

  const handleOpen = async () => {
    setIsOpen(true);
    await fetchAttempt({ variables: { attemptId } });
  };

  const handleClose = () => {
    setIsOpen(false);
    setTraitsExpanded(false);
  };

  const traitScores = attempt?.traitScores && typeof attempt.traitScores === 'object'
    ? attempt.traitScores
    : {};

  const blend = attempt?.blend && typeof attempt.blend === 'object'
    ? attempt.blend
    : {};

  const maxScore = Math.max(0, ...Object.values(traitScores));
  const desc = attempt?.profile ? PROFILE_DESCRIPTIONS[attempt.profile] : null;
  const config = attempt?.profile ? (ARCHETYPE_CONFIG[attempt.profile] || {}) : {};
  const gradient = config.gradient || DEFAULT_GRADIENT;

  const modal = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.modalOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className={styles.modalContent}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.25 }}
          >
            <div className={styles.modalActions}>
              {attempt?.type === 'trait-assessment' && (
                <button
                  className={styles.downloadButton}
                  onClick={handleDownloadPDF}
                  disabled={pdfLoading}
                  aria-label="Download PDF"
                >
                  <FaDownload size={14} />
                  <span>{pdfLoading ? 'Generating...' : 'Download PDF'}</span>
                </button>
              )}
              <button className={styles.closeButton} onClick={handleClose} aria-label="Close">
                <AiOutlineClose size={18} />
              </button>
            </div>

            {loading && <p className={styles.stateMessage}>Loading...</p>}
            {error && <p className={styles.stateMessage}>Failed to load assessment.</p>}
            {!loading && !error && !attempt && (
              <p className={styles.stateMessage}>No data found for this attempt.</p>
            )}
            {attempt && attempt.type !== 'trait-assessment' && (
              <p className={styles.stateMessage}>This is not a trait assessment.</p>
            )}

            {attempt && attempt.type === 'trait-assessment' && (
              <div className={styles.page}>

                {/* Hero */}
                <div className={styles.hero}>
                  <div
                    className={styles.avatarPlaceholder}
                    style={{ background: gradient }}
                  >
                    <FaUser className={styles.avatarIcon} />
                    <span className={styles.avatarLabel}>Avatar{'\n'}Coming Soon</span>
                  </div>

                  <div className={styles.heroInfo}>
                    <span className={styles.archetypeLabel}>Athlete Archetype</span>
                    <h1 className={styles.archetypeName}>{attempt.profile}</h1>
                    <span className={styles.confidenceBadge}>
                      {(attempt.confidence * 100).toFixed(1)}% match
                    </span>
                    {desc?.dominantTraits?.length > 0 && (
                      <div className={styles.dominantTraits}>
                        {desc.dominantTraits.map(t => (
                          <span key={t} className={styles.traitPill}>{t}</span>
                        ))}
                      </div>
                    )}
                    <div className={styles.heroDivider} />
                    {desc?.summary && (
                      <p className={styles.heroSummary}>{desc.summary}</p>
                    )}
                  </div>
                </div>

                {/* Info Cards */}
                {desc && (
                  <div className={styles.infoGrid}>
                    <div className={styles.infoCard}>
                      <h3 className={styles.infoCardTitle}>Coaching Style</h3>
                      <p className={styles.infoCardBody}>{desc.coachingStyle}</p>
                    </div>
                    <div className={styles.infoCard}>
                      <h3 className={styles.infoCardTitle}>Challenges</h3>
                      <p className={styles.infoCardBody}>{desc.challenges}</p>
                    </div>
                    <div className={styles.infoCard}>
                      <h3 className={styles.infoCardTitle}>Ideal Environment</h3>
                      <p className={styles.infoCardBody}>{desc.idealEnvironment}</p>
                    </div>
                  </div>
                )}

                {/* Data */}
                <div className={styles.dataGrid}>

                  {/* Profile Blend */}
                  <div className={styles.dataCard}>
                    <h3 className={styles.dataCardTitle}>Profile Blend</h3>
                    <div className={styles.blendList}>
                      {Object.entries(blend)
                        .sort((a, b) => b[1] - a[1])
                        .map(([name, pct], i) => (
                          <div key={name} className={styles.blendItem}>
                            <div className={styles.blendMeta}>
                              <Tooltip text={PROFILE_DESCRIPTIONS[name]?.summary || name} wide>
                                <span className={styles.blendName}>{name}</span>
                              </Tooltip>
                              <span className={styles.blendPct}>{(pct * 100).toFixed(1)}%</span>
                            </div>
                            <div className={styles.blendBarBg}>
                              <motion.div
                                className={styles.blendBarFill}
                                style={i === 0 ? { background: gradient } : undefined}
                                initial={{ width: 0 }}
                                animate={{ width: `${pct * 100}%` }}
                                transition={{ duration: 0.6, ease: 'easeOut', delay: i * 0.07 }}
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Trait Scores */}
                  <div className={styles.dataCard}>
                    <h3 className={styles.dataCardTitle}>Trait Scores</h3>
                    <div className={styles.traitList}>
                      {Object.entries(traitScores)
                        .sort((a, b) => b[1] - a[1])
                        .filter((_, i) => traitsExpanded || i < TOP_COUNT)
                        .map(([trait, score], i) => {
                          const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
                          return (
                            <div key={trait} className={styles.traitItem}>
                              <div className={styles.traitMeta}>
                                <span className={styles.traitName}>
                                  {trait}
                                  {i < TOP_COUNT && <span className={styles.topBadge}>TOP</span>}
                                </span>
                                <span className={styles.traitScore}>{score}</span>
                              </div>
                              <div className={styles.traitBarBg}>
                                <motion.div
                                  className={styles.traitBarFill}
                                  style={i < TOP_COUNT ? { background: gradient } : undefined}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${pct}%` }}
                                  transition={{ duration: 0.5, ease: 'easeOut', delay: i * 0.04 }}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                    {Object.keys(traitScores).length > TOP_COUNT && (
                      <button
                        className={styles.expandTraitsButton}
                        onClick={() => setTraitsExpanded(prev => !prev)}
                      >
                        {traitsExpanded
                          ? 'Show less ▲'
                          : `Show all ${Object.keys(traitScores).length} traits ▼`}
                      </button>
                    )}
                  </div>

                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <button className={globalButtonStyles.button} onClick={handleOpen}>
        View Details
      </button>
      {createPortal(modal, document.getElementById('modal-root'))}
    </>
  );
};

export default ViewTraitQuizDetailsButton;
